import React, { useState, useCallback, useRef } from "react";
import { Column, Table } from "@tanstack/react-table";
import { Issue } from "@adsk/acc-issues-types";
import SearchField from "@adsk/alloy-react-search-field";
import Divider from "@adsk/alloy-react-divider";
import { Flyout } from "@adsk/alloy-react-tooltip";
import { GearIcon } from "@adsk/alloy-react-icon";
import {
  TableConfigurationDensity,
  TableConfigurationReset,
  TableConfigurationDnDColumns,
} from "@adsk/alloy-react-table";
import type { TableSize } from "@adsk/alloy-react-table/es/types";
import { StyledTableConfigurationMenu, DragNDrop } from "./issuesTableStyles";
import styled from "styled-components";
import { useGetInitialColumnVisibility } from "./useTableColumnDefs";
import { useClearTableState } from "./useTableLocalStorage";
import { SELECT_COLUMN, ACTIONS_COLUMN } from "./columnKeys";
import { useTranslations } from "@adsk/acc-issues-translations";

interface TableConfigurationMenuProps {
  table: Table<Issue>;
  tableSize: TableSize;
  setTableSize: (size: TableSize) => void;
}

const NON_REORDERABLE_COLUMNS = new Set([SELECT_COLUMN, ACTIONS_COLUMN]);

const TableConfigMenu = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
`;

const getColumnDisplayName = (column: Column<Issue, unknown>): string => {
  const meta = column.columnDef.meta as any;
  return meta?.headerText || column.id;
};

// Ensure the columns in the configuration menu are in the same order as they appear in the table.
const sortColumnsByOrder = (
  columns: Column<Issue, unknown>[],
  order: string[]
): Column<Issue, unknown>[] => {
  if (order.length === 0) return columns;

  return [...columns].sort((a, b) => {
    const aIndex = order.indexOf(a.id);
    const bIndex = order.indexOf(b.id);
    const aPos = aIndex === -1 ? Infinity : aIndex;
    const bPos = bIndex === -1 ? Infinity : bIndex;
    return aPos - bPos;
  });
};

const getReorderableColumns = (
  table: Table<Issue>,
  filterValue: string
): Column<Issue, unknown>[] => {
  const allColumns = table.getAllLeafColumns();
  const columnOrder = table.getState().columnOrder || [];

  const reorderableColumns = allColumns.filter(
    col => !NON_REORDERABLE_COLUMNS.has(col.id)
  );

  const sortedColumns = sortColumnsByOrder(reorderableColumns, columnOrder);

  if (!filterValue) return sortedColumns;

  const searchTerm = filterValue.toLowerCase();
  return sortedColumns.filter(col =>
    getColumnDisplayName(col).toLowerCase().includes(searchTerm)
  );
};

const buildColumnOrder = (
  reorderedColumns: string[],
  allColumnIds: string[]
): string[] => {
  const selectColumn = allColumnIds.find(id => id === SELECT_COLUMN);
  const actionsColumn = allColumnIds.find(id => id === ACTIONS_COLUMN);

  const otherColumns = allColumnIds.filter(
    id => !reorderedColumns.includes(id) && !NON_REORDERABLE_COLUMNS.has(id)
  );

  return [
    ...(selectColumn ? [selectColumn] : []),
    ...reorderedColumns.filter(id => !NON_REORDERABLE_COLUMNS.has(id)),
    ...otherColumns,
    ...(actionsColumn ? [actionsColumn] : []),
  ];
};

export const TableConfigurationMenu: React.FC<TableConfigurationMenuProps> = ({
  table,
  tableSize,
  setTableSize,
}) => {
  const { T } = useTranslations();
  const [filterValue, setFilterValue] = useState("");
  const initialSize = useRef(tableSize);
  const initialColumnVisibility = useGetInitialColumnVisibility();
  const clearTableState = useClearTableState();

  const columns = getReorderableColumns(table, filterValue);

  const handleSetColumnOrder = useCallback(
    (updater: string[] | ((prev: string[]) => string[])) => {
      const currentOrder = table.getState().columnOrder || [];
      const allColumnIds = table.getAllLeafColumns().map(col => col.id);
      const baseOrder = currentOrder.length > 0 ? currentOrder : allColumnIds;

      const newOrder =
        typeof updater === "function" ? updater(baseOrder) : updater;

      const finalOrder = buildColumnOrder(newOrder, allColumnIds);
      table.setColumnOrder(finalOrder);
    },
    [table]
  );

  const reset = useCallback(() => {
    setTableSize(initialSize.current);
    table.setColumnVisibility(initialColumnVisibility);
    table.resetColumnOrder();
    clearTableState();
  }, [table, setTableSize, initialColumnVisibility, clearTableState]);

  return (
    <Flyout
      placement={Flyout.PLACEMENTS.BOTTOM_END}
      trigger={Flyout.TRIGGER_EVENTS.CLICK}
      shouldWrapChildren
      content={
        <StyledTableConfigurationMenu data-testid="TableConfigurationMenu__dropdown">
          <div style={{ flexShrink: 0 }}>
            <SearchField
              placeholder={
                T.translate("issueTable.searchColumnsPlaceholderText") as string
              }
              value={filterValue}
              onChange={setFilterValue}
            />
          </div>
          <DragNDrop>
            <TableConfigurationDnDColumns
              columns={columns as any}
              setColumnOrder={handleSetColumnOrder}
              getColumnName={getColumnDisplayName as any}
            />
          </DragNDrop>
          <Divider />
          <TableConfigurationDensity
            tableSize={tableSize}
            setSize={setTableSize}
          />
          <Divider />
          <TableConfigurationReset reset={reset} />
        </StyledTableConfigurationMenu>
      }
    >
      <TableConfigMenu data-testid="TableConfigurationButton">
        <GearIcon />
      </TableConfigMenu>
    </Flyout>
  );
};
